"use client";

import type { FC } from "react";
import { PhoneIcon } from "@heroicons/react/24/outline";
import type { Inquiry } from "cmw-client";
import { DataTable, DateCell, TruncatedCell, type Column } from "@/components/data-table";
import { InquiryNewBadge } from "../inquiry-new-badge";
import { OpenInquiryAction } from "../open-inquiry-action";
import { useInquiriesTable } from "../../hooks/use-inquiries";

/* Widths mirror the Figma table (2:12876); "Clients" has none so it absorbs the slack. */
const columns: Column<Inquiry>[] = [
  {
    id: "receivedAt",
    header: "Received",
    sortKey: "createdAt",
    width: 280,
    cell: (row) => (
      <span className="flex flex-wrap items-center gap-8">
        <DateCell value={row.createdAt} showTime={false} />
        {row.status === "new" && <InquiryNewBadge />}
      </span>
    ),
  },
  {
    id: "fullName",
    header: "Clients",
    sortKey: "fullName",
    cell: (row) => <TruncatedCell value={row.fullName} />,
  },
  {
    id: "phone",
    header: "Mobile Number",
    width: 220,
    cell: (row) => (
      <span className="flex items-center gap-8 whitespace-nowrap">
        <PhoneIcon aria-hidden className="size-16 shrink-0 text-neutral-black/70" />
        {row.phone}
      </span>
    ),
  },
  { id: "action", header: "Action", width: 190, cell: (row) => <OpenInquiryAction inquiry={row} /> },
];

type InquiriesTableProps = {
  pageSize?: number;
  enableSearch?: boolean;
};

/** The practitioner's inbox. Backed by `GET /inquiries`. Also used, compact, on the dashboard overview. */
export const InquiriesTable: FC<InquiriesTableProps> = ({ pageSize = 10, enableSearch = true }) => (
  <DataTable
    {...useInquiriesTable()}
    columns={columns}
    rowKey={(row) => row.id}
    summaryLabel="enquiries"
    searchPlaceholder="Search by name or email"
    emptyState="No enquiries yet."
    pageSize={pageSize}
    enableSearch={enableSearch}
    stickyLastColumn
  />
);
